import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, ClipboardList, Loader2, AlertCircle, X, ChevronUp, ChevronDown, ChevronsUpDown, Pencil, Check } from 'lucide-react';
import { useCurrentUser } from '../../context/UserContext';
import { useAnnotationTaskList } from '../../hooks/useAnnotationTask';
import * as annotationApi from '../../services/annotationApi';
import { getUsers, updateSessionLanguage, getProjects, updateSessionProject } from '../../services/api';
import {
  TaskListItem,
  TaskStatus,
  TASK_STATUS_LABELS,
  TASK_STATUS_COLORS,
} from '../../types/annotation';
import { UserEmail } from '../../components/common/UserEmail';
import { Pager } from '../../components/common/Pager';
import { StickyScrollX } from '../../components/common/StickyScrollX';

const PAGE_SIZE = 50;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(seconds?: number): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatUploadDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd  = String(d.getDate()).padStart(2, '0');
  const mm  = String(d.getMonth() + 1).padStart(2, '0');
  const yy  = String(d.getFullYear()).slice(-2);
  const hh  = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yy}-${hh}-${min}`;
}

// "Transcribed Date" = submittedAt if the task is submitted, otherwise the
// last-modified timestamp from the task row (which gets bumped on claim,
// utterance saves via task touch, status changes, etc.). Returns '—' when
// the task hasn't been touched by a transcriber yet (pending / unclaimed).
function transcribedAtOf(t: TaskListItem): string | undefined {
  return t.submittedAt ?? t.updatedAt;
}

// ─── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TaskStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${TASK_STATUS_COLORS[status]}`}
    >
      {TASK_STATUS_LABELS[status]}
    </span>
  );
}

// ─── Create Task Modal (Admin only) ────────────────────────────────────────────

interface CreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  supervisors: Array<{ userId: string; name?: string; email?: string }>;
}

function CreateTaskModal({ open, onClose, supervisors }: CreateTaskModalProps) {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState('');
  const [enumeratorId, setEnumeratorId] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      annotationApi.createTask({
        sessionId: sessionId.trim(),
        enumeratorId: enumeratorId.trim() || undefined,
        supervisorId: supervisorId || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
      setToast({ type: 'success', message: 'Task created successfully.' });
      setSessionId('');
      setEnumeratorId('');
      setSupervisorId('');
      setTimeout(() => {
        setToast(null);
        onClose();
      }, 1200);
    },
    onError: (err: Error) => {
      setToast({ type: 'error', message: err.message });
    },
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Create Annotation Task</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {toast && (
            <div
              className={`px-3 py-2 rounded-lg text-sm ${
                toast.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {toast.message}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Session ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="Enter session ID"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Enumerator ID (optional)
            </label>
            <input
              type="text"
              value={enumeratorId}
              onChange={(e) => setEnumeratorId(e.target.value)}
              placeholder="Defaults to session's enumerator"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Supervisor (optional)
            </label>
            <select
              value={supervisorId}
              onChange={(e) => setSupervisorId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
            >
              <option value="">— None —</option>
              {supervisors.map((s) => (
                <option key={s.userId} value={s.userId}>
                  {s.name ?? s.email ?? s.userId}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg font-medium bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!sessionId.trim() || createMutation.isPending}
            className="px-4 py-2 text-sm rounded-lg font-medium bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {createMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Create Task
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sort ──────────────────────────────────────────────────────────────────────

// Sortable columns. These map 1:1 to the backend `sortBy` param, which does the
// ordering in SQL over the whole dataset (priority order + NULLS-LAST for the
// name/metadata columns are handled server-side in list-tasks.ts).
type SortField = 'priority' | 'createdAt' | 'duration' | 'language' | 'project' | 'transcribedBy' | 'transcribedAt';
type SortDir = 'asc' | 'desc';

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <ChevronsUpDown className="w-3 h-3 text-gray-400 inline ml-1" />;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3 h-3 text-violet-500 inline ml-1" />
    : <ChevronDown className="w-3 h-3 text-violet-500 inline ml-1" />;
}

// ─── Editable Language Cell (admin only) ──────────────────────────────────────

function EditableLanguageCell({
  task,
  editable,
  variant,
}: {
  task: TaskListItem;
  editable: boolean;
  variant: 'desktop' | 'mobile';
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.language ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (next: string) => updateSessionLanguage(task.sessionId, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
      setEditing(false);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const startEdit = () => {
    setDraft(task.language ?? '');
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setError('Language cannot be empty');
      return;
    }
    if (trimmed === (task.language ?? '').trim()) {
      setEditing(false);
      return;
    }
    mutation.mutate(trimmed);
  };

  if (!editable) {
    return variant === 'desktop'
      ? <>{task.language ?? '—'}</>
      : <>{task.language ?? '—'}</>;
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          disabled={mutation.isPending}
          className="px-2 py-1 text-sm border border-violet-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent w-32"
        />
        <button
          onClick={save}
          disabled={mutation.isPending}
          className="p-1 text-violet-600 hover:bg-violet-50 rounded-md transition-colors disabled:opacity-50"
          title="Save"
        >
          {mutation.isPending
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Check className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={cancel}
          disabled={mutation.isPending}
          className="p-1 text-gray-400 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          title="Cancel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        {error && (
          <span className="text-xs text-red-600 ml-1" title={error}>!</span>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={startEdit}
      className="group inline-flex items-center gap-1.5 text-left hover:text-violet-700 transition-colors"
      title="Edit language"
    >
      <span>{task.language ?? '—'}</span>
      <Pencil className="w-3 h-3 text-gray-300 group-hover:text-violet-500 transition-colors" />
    </button>
  );
}

// ─── Editable Project Cell (admin only) ───────────────────────────────────────

function EditableProjectCell({
  task,
  editable,
  projects,
  currentName,
}: {
  task: TaskListItem;
  editable: boolean;
  projects: Array<{ id: string; name: string }>;
  currentName: string;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.projectId ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (projectId: string) => updateSessionProject(task.sessionId, projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
      setEditing(false);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  if (!editable) return <>{currentName}</>;

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <select
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={mutation.isPending}
          className="px-2 py-1 text-sm border border-violet-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent max-w-[11rem]"
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          onClick={() => {
            if (!draft) { setError('Pick a project'); return; }
            if (draft === task.projectId) { setEditing(false); return; }
            mutation.mutate(draft);
          }}
          disabled={mutation.isPending}
          className="p-1 text-violet-600 hover:bg-violet-50 rounded-md transition-colors disabled:opacity-50"
          title="Save"
        >
          {mutation.isPending
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Check className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => { setEditing(false); setError(null); }}
          disabled={mutation.isPending}
          className="p-1 text-gray-400 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          title="Cancel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        {error && <span className="text-xs text-red-600 ml-1" title={error}>!</span>}
      </div>
    );
  }

  return (
    <button
      onClick={() => { setDraft(task.projectId ?? ''); setError(null); setEditing(true); }}
      className="group inline-flex items-center gap-1.5 text-left hover:text-violet-700 transition-colors"
      title="Change project"
    >
      <span>{currentName}</span>
      <Pencil className="w-3 h-3 text-gray-300 group-hover:text-violet-500 transition-colors" />
    </button>
  );
}

// ─── Task Table ────────────────────────────────────────────────────────────────

interface TaskTableProps {
  tasks: TaskListItem[];
  actionLabel: 'Annotate' | 'Review' | 'dynamic';
  onAction: (taskId: string) => void;
  canEditLanguage?: boolean;
  canEditProject?: boolean;
  projects?: Array<{ id: string; name: string }>;
  // userId → { email, role } map so the enumerator cell can render the
  // colour-coded UserEmail pill. Falls back to enumeratorName when absent.
  userLookup?: Map<string, { email?: string; role?: string }>;
  // projectId → project name, for the Project column + its sort.
  projectLookup?: Map<string, string>;
  // Sort is owned by the page (it drives the server query so it spans all
  // pages, not just the rows currently loaded). The table only reflects it and
  // reports header clicks back up.
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}

function getRowActionLabel(task: TaskListItem, defaultLabel: 'Annotate' | 'Review' | 'dynamic'): string {
  if (defaultLabel !== 'dynamic') return defaultLabel;
  if (task.status === 'submitted') return 'Review';
  if (task.status === 'approved' || task.status === 'rejected') return 'View';
  return 'Annotate';
}

function TaskTable({ tasks, actionLabel, onAction, canEditLanguage = false, canEditProject = false, projects = [], userLookup, projectLookup, sortField, sortDir, onSort }: TaskTableProps) {
  const projectNameOf = (task: TaskListItem) =>
    projectLookup?.get(task.projectId ?? '') ?? '—';
  // Single render helper shared by the Enumerator and Transcribed By cells
  // so the two columns — and the Admin/Supervisor views, which use the same
  // TaskTable but draw on different role-scoped data — present user identity
  // identically. Always shows whatever's available (name + email pill);
  // falls back to a placeholder if absolutely nothing is on the wire.
  const renderUserCell = (opts: {
    name?: string | null;
    email?: string | null;
    role?: string | null;
    fallbackText?: string;
  }) => {
    const { name, email, role, fallbackText = '—' } = opts;
    // Hide the name line when it duplicates the email — older signups
    // stored displayName as the user's email, and rendering both would
    // look like the email is shown twice.
    const showName = !!(name && name.toLowerCase() !== (email ?? '').toLowerCase());
    if (showName || email) {
      return (
        <div className="flex flex-col gap-0.5 min-w-0">
          {showName && (
            <div className="text-sm font-medium text-gray-900 truncate">{name}</div>
          )}
          {email && <UserEmail email={email} role={role ?? undefined} />}
        </div>
      );
    }
    return <span className="text-gray-400">{name ?? fallbackText}</span>;
  };

  const renderEnumeratorCell = (task: TaskListItem) => {
    // list-tasks attaches name+email+role per task from the Users table.
    // userLookup is only consulted as a last-resort fallback (e.g. if the
    // server-side enrichment didn't carry email for some reason).
    const lookup = userLookup?.get(task.enumeratorId ?? '');
    return renderUserCell({
      name: task.enumeratorName,
      email: task.enumeratorEmail ?? lookup?.email,
      role: task.enumeratorRole ?? lookup?.role,
      fallbackText: task.enumeratorId ? `${task.enumeratorId.slice(0, 10)}…` : '—',
    });
  };

  const renderTranscribedByCell = (task: TaskListItem) => {
    // Prefer `claimedBy` (currently transcribing) over `submittedBy`
    // (historical submitter). userLookup fallback covers users that
    // exist in the platform but were missing from the task-row enrichment.
    const userId = task.claimedBy ?? task.submittedBy ?? undefined;
    const lookup = userId ? userLookup?.get(userId) : undefined;
    return renderUserCell({
      name: task.claimedByName ?? task.submittedByName,
      email: task.claimedByEmail ?? task.submittedByEmail ?? lookup?.email,
      role: task.claimedByRole ?? task.submittedByRole ?? lookup?.role,
    });
  };
  // Sort state lives in the page and drives the server query, so the ordering
  // is applied across the whole dataset — the table just renders the rows the
  // server returned for the current page. Header clicks call onSort.
  const handleSort = onSort;
  const sorted = tasks;

  // `sticky top-0` keeps the column headers pinned to the top of the
  // scroll container when the user scrolls through rows. bg-gray-50 +
  // z-10 ensure the headers cover scrolling rows behind them, and the
  // bottom border lives on each cell (not the <tr>) so it stays glued
  // to the sticky cells instead of drifting to the row's natural
  // page-flow position.
  const thClass = 'sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide';
  const sortableThClass = `${thClass} cursor-pointer select-none hover:text-gray-700`;

  return (
    <>
      {/* Mobile card list — hidden on md+ (covers phones in landscape too) */}
      <div className="md:hidden space-y-3">
        {sorted.map((task) => (
          <div key={task.taskId} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Uploaded</span>
                <p className="font-mono text-xs text-gray-700 mt-0.5">{formatUploadDateTime(task.createdAt)}</p>
              </div>
              <StatusBadge status={task.status} />
            </div>
            <div className="mb-2">
              <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {task.sessionId ? `${task.sessionId.slice(0, 16)}…` : '—'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-500 mb-4">
              <div>
                <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Enumerator</span>
                <p className="text-gray-700 mt-0.5 truncate">
                  {renderEnumeratorCell(task)}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Language</span>
                <p className="text-gray-700 mt-0.5">
                  <EditableLanguageCell task={task} editable={canEditLanguage} variant="mobile" />
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Project</span>
                <p className="text-gray-700 mt-0.5 truncate">
                  <EditableProjectCell task={task} editable={canEditProject} projects={projects} currentName={projectNameOf(task)} />
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Duration</span>
                <p className="font-mono text-gray-700 mt-0.5">{formatDuration(task.duration)}</p>
              </div>
              <div>
                <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Transcribed By</span>
                <div className="text-gray-700 mt-0.5">{renderTranscribedByCell(task)}</div>
              </div>
              <div>
                <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Transcribed Date</span>
                <p className="font-mono text-gray-700 mt-0.5">{formatUploadDateTime(transcribedAtOf(task))}</p>
              </div>
              {task.sessionLocation && (
                <div className="col-span-2">
                  <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Location</span>
                  <p className="text-gray-700 mt-0.5">
                    {task.sessionLocation.state}, {task.sessionLocation.district}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => onAction(task.taskId)}
              className="w-full py-2 text-sm rounded-lg font-medium bg-violet-600 hover:bg-violet-700 text-white transition-colors"
            >
              {getRowActionLabel(task, actionLabel)}
            </button>
          </div>
        ))}
      </div>

      {/* Desktop table — hidden on phones (including landscape).
          StickyScrollX renders a viewport-pinned horizontal scrollbar above
          the table AND bounds the inner area so the column headers can
          stick to the top as the user scrolls through rows. We can't put
          `overflow-hidden` on this wrapper — it would clip the sticky
          scroll proxy as soon as the wrapper's top scrolls above the
          viewport. min-w on the <table> stops the browser from squashing
          columns to fit. */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-xl">
        <StickyScrollX maxHeight="calc(100vh - 280px)">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className={sortableThClass} onClick={() => handleSort('createdAt')}>
                Uploaded <SortIcon field="createdAt" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className={thClass}>Session ID</th>
              <th className={thClass}>Enumerator</th>
              <th className={sortableThClass} onClick={() => handleSort('language')}>
                Language <SortIcon field="language" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className={sortableThClass} onClick={() => handleSort('project')}>
                Project <SortIcon field="project" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className={sortableThClass} onClick={() => handleSort('duration')}>
                Duration <SortIcon field="duration" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className={sortableThClass} onClick={() => handleSort('priority')}>
                Status <SortIcon field="priority" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className={sortableThClass} onClick={() => handleSort('transcribedBy')}>
                Transcribed By <SortIcon field="transcribedBy" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className={sortableThClass} onClick={() => handleSort('transcribedAt')}>
                Transcribed Date <SortIcon field="transcribedAt" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className={thClass}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((task) => (
              <tr key={task.taskId} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                  {formatUploadDateTime(task.createdAt)}
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
                    {task.sessionId
                      ? task.sessionId.length > 12
                        ? `${task.sessionId.slice(0, 12)}…`
                        : task.sessionId
                      : '—'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700">
                  {renderEnumeratorCell(task)}
                </td>
                <td className="px-4 py-3 text-gray-500">
                  <EditableLanguageCell task={task} editable={canEditLanguage} variant="desktop" />
                </td>
                <td className="px-4 py-3 text-gray-700">
                  <EditableProjectCell task={task} editable={canEditProject} projects={projects} currentName={projectNameOf(task)} />
                </td>
                <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                  {formatDuration(task.duration)}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={task.status} />
                </td>
                <td className="px-4 py-3 text-gray-700">
                  {renderTranscribedByCell(task)}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                  {formatUploadDateTime(transcribedAtOf(task))}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onAction(task.taskId)}
                    className="px-3 py-1.5 text-xs rounded-lg font-medium bg-violet-600 hover:bg-violet-700 text-white transition-colors"
                  >
                    {getRowActionLabel(task, actionLabel)}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </StickyScrollX>
      </div>
    </>
  );
}

// ─── Filter Bar ────────────────────────────────────────────────────────────────

interface FilterBarProps {
  statusFilter: string;
  projectFilter: string;
  projects: Array<{ id: string; name: string }>;
  enumeratorFilter: string;
  languageFilter: string;
  transcribedByFilter: string;
  sessionIdFilter: string;
  onStatusChange: (v: string) => void;
  onProjectChange: (v: string) => void;
  onEnumeratorChange: (v: string) => void;
  onLanguageChange: (v: string) => void;
  onTranscribedByChange: (v: string) => void;
  onSessionIdChange: (v: string) => void;
}

function FilterBar({
  statusFilter,
  projectFilter,
  projects,
  enumeratorFilter,
  languageFilter,
  transcribedByFilter,
  sessionIdFilter,
  onStatusChange,
  onProjectChange,
  onEnumeratorChange,
  onLanguageChange,
  onTranscribedByChange,
  onSessionIdChange,
}: FilterBarProps) {
  const ALL_STATUSES: Array<{ value: string; label: string }> = [
    { value: '', label: 'All Statuses' },
    ...Object.entries(TASK_STATUS_LABELS).map(([value, label]) => ({ value, label })),
  ];

  return (
    <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
      <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <select
        value={statusFilter}
        onChange={(e) => onStatusChange(e.target.value)}
        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
      >
        {ALL_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      <select
        value={projectFilter}
        onChange={(e) => onProjectChange(e.target.value)}
        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white max-w-[12rem]"
      >
        <option value="">All Projects</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={enumeratorFilter}
          onChange={(e) => onEnumeratorChange(e.target.value)}
          placeholder="Enumerator..."
          className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent w-36"
        />
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={languageFilter}
          onChange={(e) => onLanguageChange(e.target.value)}
          placeholder="Language..."
          className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent w-36"
        />
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={transcribedByFilter}
          onChange={(e) => onTranscribedByChange(e.target.value)}
          placeholder="Transcribed By..."
          className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent w-40"
        />
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={sessionIdFilter}
          onChange={(e) => onSessionIdChange(e.target.value)}
          placeholder="Session ID..."
          className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent w-44"
        />
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function AnnotationTaskListPage() {
  const { role } = useCurrentUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'review' | 'all'>('review');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [enumeratorFilter, setEnumeratorFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [transcribedByFilter, setTranscribedByFilter] = useState('');
  const [sessionIdFilter, setSessionIdFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // Sort is owned here (not in TaskTable) so it feeds the server query and
  // therefore orders the ENTIRE dataset, not just the loaded page. Default is
  // the priority sort (revision_needed → in_progress → …); the server ignores
  // direction for priority.
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  // 1-indexed page; resets to 1 whenever a filter or sort changes so admins
  // don't land on an empty/misordered page after narrowing the result set.
  const [allPage, setAllPage] = useState(1);
  const [reviewPage, setReviewPage] = useState(1);

  // Transcribers route through the enumerator branch — same self-service
  // task list, no supervisor or create-task affordances. The server filters
  // tasks correctly based on the new transcriber_assignments table.
  const isEnumerator = role === 'enumerator' || role === 'transcriber';
  const isSupervisor = role === 'supervisor';
  const isAdmin = role === 'admin';

  // Every filter + the sort is now applied server-side (spanning all pages), so
  // any change must reset pagination to page 1.
  useEffect(() => {
    setAllPage(1);
    setReviewPage(1);
  }, [statusFilter, projectFilter, enumeratorFilter, languageFilter, transcribedByFilter, sessionIdFilter, sortField, sortDir]);

  // Shared server-side filter/sort params. All filtering + sorting happens in
  // SQL so the result spans the whole dataset; the browser only ever holds one
  // page. Each (params, page) tuple is cached independently by React Query.
  const commonQueryParams = {
    projectId: projectFilter || undefined,
    enumerator: enumeratorFilter.trim() || undefined,
    language: languageFilter.trim() || undefined,
    transcribedBy: transcribedByFilter.trim() || undefined,
    sessionId: sessionIdFilter.trim() || undefined,
    sortBy: sortField,
    sortDir,
  };

  const { data: tasksResp, isLoading, isError } = useAnnotationTaskList({
    ...commonQueryParams,
    status: statusFilter ? (statusFilter as TaskStatus) : undefined,
    limit: PAGE_SIZE,
    offset: (allPage - 1) * PAGE_SIZE,
  });
  const tasks = tasksResp?.tasks ?? [];
  const tasksTotal = tasksResp?.total ?? 0;

  // Supervisor / admin review queue: a parallel paged query scoped to
  // submitted-status tasks. Drives the Review Queue tab content + badge.
  const { data: reviewResp } = useAnnotationTaskList(
    isSupervisor || isAdmin
      ? {
          ...commonQueryParams,
          status: 'submitted',
          limit: PAGE_SIZE,
          offset: (reviewPage - 1) * PAGE_SIZE,
        }
      : undefined,
  );
  const reviewQueueTasks = reviewResp?.tasks ?? [];
  const reviewQueueTotal = reviewResp?.total ?? 0;

  // Fetch all users once for role-coloured email lookup in the enumerator
  // cell. Cheap on the wire (one /users call) and React-Query caches it
  // across all pages that need it. limit=5000 bypasses the backend's
  // default-50 page so admin sees every user, not just the most recent.
  const { data: allUsersData } = useQuery({
    queryKey: ['users', 'all-for-lookup'],
    queryFn: () => getUsers({ limit: '5000' }).then((d) => d.users ?? d ?? []),
    staleTime: 5 * 60 * 1000,
    enabled: isSupervisor || isAdmin,
  });
  const userLookup = useMemo(() => {
    const map = new Map<string, { email?: string; role?: string }>();
    (allUsersData ?? []).forEach((u: { userId?: string; email?: string; role?: string }) => {
      if (u.userId) map.set(u.userId, { email: u.email, role: u.role });
    });
    return map;
  }, [allUsersData]);

  // Projects (the folders enumerators upload into) — used to render/sort/filter
  // the Project column by human-readable name instead of the raw project_id UUID
  // the task row carries. Loaded once; cheap.
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects({ status: 'all' }),
    enabled: isAdmin || isSupervisor,
  });
  const projectLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of (projectsData?.projects ?? []) as Array<{ projectId: string; name: string }>) {
      map.set(p.projectId, p.name);
    }
    return map;
  }, [projectsData]);
  const projectOptions = useMemo(
    () =>
      ((projectsData?.projects ?? []) as Array<{ projectId: string; name: string }>)
        .map((p) => ({ id: p.projectId, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [projectsData],
  );

  const { data: supervisors = [] } = useQuery({
    queryKey: ['users', 'supervisor'],
    queryFn: () => getUsers({ role: 'supervisor', approvalStatus: 'approved' }).then((d) => d.users ?? d ?? []),
    enabled: isAdmin,
  });

  const handleAction = (taskId: string) => {
    navigate(`/annotation/session/${taskId}`);
  };

  // All filtering + sorting is applied server-side (status, project, enumerator,
  // language, transcribedBy, and the sort), so the rows the server returned for
  // this page are already the correct, fully-scoped slice — nothing to narrow
  // client-side.
  const filteredTasks = tasks;
  const reviewTasks = reviewQueueTasks;

  // ── Loading ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-10 h-10 text-red-500" />
        <p className="text-gray-600 font-medium">Failed to load tasks.</p>
      </div>
    );
  }

  // ── Enumerator view ────────────────────────────────────────────────────────

  if (isEnumerator) {
    return (
      <div className="space-y-6 pb-24 md:pb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Annotation Tasks</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Sessions assigned to you for annotation
            </p>
          </div>
        </div>

        {tasksTotal === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No tasks assigned yet.</p>
            <p className="text-gray-400 text-sm mt-1">
              Your recordings will appear here once assigned for annotation.
            </p>
          </div>
        ) : (
          <>
            <TaskTable tasks={filteredTasks} actionLabel="Annotate" onAction={handleAction} userLookup={userLookup} projectLookup={projectLookup} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            <Pager total={tasksTotal} page={allPage} pageSize={PAGE_SIZE} onChange={setAllPage} />
          </>
        )}
      </div>
    );
  }

  // ── Supervisor view ────────────────────────────────────────────────────────

  if (isSupervisor) {
    return (
      <div className="space-y-6 pb-24 md:pb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Annotation Tasks</h1>
            <p className="text-sm text-gray-500 mt-0.5">Review and manage your team's tasks</p>
          </div>
        </div>

        <FilterBar
          statusFilter={statusFilter}
          projectFilter={projectFilter}
          projects={projectOptions}
          enumeratorFilter={enumeratorFilter}
          languageFilter={languageFilter}
          transcribedByFilter={transcribedByFilter}
          sessionIdFilter={sessionIdFilter}
          onStatusChange={setStatusFilter}
          onProjectChange={setProjectFilter}
          onEnumeratorChange={setEnumeratorFilter}
          onLanguageChange={setLanguageFilter}
          onTranscribedByChange={setTranscribedByFilter}
          onSessionIdChange={setSessionIdFilter}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { id: 'review' as const, label: 'Review Queue' },
              { id: 'all' as const, label: 'All Tasks' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-violet-600 text-violet-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.id === 'review' && reviewQueueTotal > 0 && (
                  <span className="ml-2 bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {reviewQueueTotal}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'review' ? (
          reviewQueueTotal === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No tasks awaiting review.</p>
              <p className="text-gray-400 text-sm mt-1">
                Tasks submitted by your enumerators will appear here.
              </p>
            </div>
          ) : (
            <>
              <TaskTable tasks={reviewTasks} actionLabel="Review" onAction={handleAction} userLookup={userLookup} projectLookup={projectLookup} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Pager total={reviewQueueTotal} page={reviewPage} pageSize={PAGE_SIZE} onChange={setReviewPage} />
            </>
          )
        ) : tasksTotal === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No tasks found.</p>
          </div>
        ) : (
          <>
            <TaskTable tasks={filteredTasks} actionLabel="dynamic" onAction={handleAction} userLookup={userLookup} projectLookup={projectLookup} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            <Pager total={tasksTotal} page={allPage} pageSize={PAGE_SIZE} onChange={setAllPage} />
          </>
        )}
      </div>
    );
  }

  // ── Admin view ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Annotation Tasks</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {tasksTotal} task{tasksTotal !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Task
        </button>
      </div>

      <FilterBar
        statusFilter={statusFilter}
        projectFilter={projectFilter}
        projects={projectOptions}
        enumeratorFilter={enumeratorFilter}
        languageFilter={languageFilter}
        transcribedByFilter={transcribedByFilter}
        sessionIdFilter={sessionIdFilter}
        onStatusChange={setStatusFilter}
        onProjectChange={setProjectFilter}
        onEnumeratorChange={setEnumeratorFilter}
        onLanguageChange={setLanguageFilter}
        onTranscribedByChange={setTranscribedByFilter}
        onSessionIdChange={setSessionIdFilter}
      />

      {tasksTotal === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No annotation tasks created yet.</p>
          <p className="text-gray-400 text-sm mt-1 mb-4">
            Create a task from a completed session to start annotation.
          </p>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Task
          </button>
        </div>
      ) : (
        <>
          <TaskTable tasks={filteredTasks} actionLabel="dynamic" onAction={handleAction} canEditLanguage canEditProject projects={projectOptions} userLookup={userLookup} projectLookup={projectLookup} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <Pager total={tasksTotal} page={allPage} pageSize={PAGE_SIZE} onChange={setAllPage} />
        </>
      )}

      <CreateTaskModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        supervisors={supervisors}
      />
    </div>
  );
}
